import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { 
  Search, 
  PenSquare, 
  Plus, 
  Loader2, 
  MapPin,
  Check, 
  X,
  Menu,
  Clock,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { deviceStore, Device } from '@/stores/deviceStore';
import MainLayout from '@/components/layouts/MainLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { authStore } from '@/stores/authStore';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import 'leaflet/dist/leaflet.css';

// Add CSS for mobile specific styling
import './DeviceManagementPage.css';

// Get the API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_IOT || '';

// Extend Device interface with additional properties
interface DeviceWithCoordinates extends Device {
  lat?: number;
  lng?: number;
  lights?: number;
  cpl?: number;
}

// MapPicker component for selecting lat/lng coordinates
const MapPicker = ({ 
  initialPosition, 
  onPositionChange 
}: { 
  initialPosition: [number, number], 
  onPositionChange: (lat: number, lng: number) => void 
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [position, setPosition] = useState<[number, number]>(initialPosition);
  
  const ClickHandler = () => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        setPosition([lat, lng]);
        onPositionChange(lat, lng);
      },
    });
    
    return (
      <Marker position={position} />
    );
  };

  return (
    <div className="h-[250px] sm:h-[300px] w-full mt-2 sm:mt-4 rounded-md overflow-hidden border">
      <div className="relative h-full">
        <MapContainer 
          center={position} 
          zoom={13} 
          style={{ height: '100%', width: '100%' }}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <ClickHandler />
        </MapContainer>
        
        {isMobile && (
          <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
            <div className="bg-white/75 text-sm py-1 px-2 rounded-md inline-block">
              {t('devices.tapToSetLocation', 'Tap to set location')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DeviceManagementPage = observer(() => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for device actions
  const [syncingDevice, setSyncingDevice] = useState<string | null>(null);
  const [isRebootDialogOpen, setIsRebootDialogOpen] = useState(false);
  const [rebootDevice, setRebootDevice] = useState<Device | null>(null);
  
  // Detect mobile devices
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmallMobile = useMediaQuery('(max-width: 640px)');
  
  // View mode for mobile (table or card)
  const [viewMode, setViewMode] = useState<'table' | 'card'>(isMobile ? 'card' : 'table');
  
  // Device edit/create state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentDevice, setCurrentDevice] = useState<DeviceWithCoordinates | null>(null);
  
  // Update view mode when screen size changes
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);
  
  // Form state
  const [formData, setFormData] = useState<DeviceWithCoordinates>({
    device_id: '',
    pillarno: '',
    lng: 0,
    lat: 0,
    area: '',
    point: '',
    relay: 1,
    lights: 0,
    cpl: 0,
  });

  // Load devices on component mount
  useEffect(() => {
    deviceStore.loadDevices();
  }, []);

  // Get authenticated API headers
  const getHeaders = () => {
    return {
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`
      }
    };
  };

  // Filter devices based on search term
  const filteredDevices = deviceStore.devices.filter(device => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (device.device_id && device.device_id.toLowerCase().includes(searchLower)) ||
      (device.area && device.area.toLowerCase().includes(searchLower)) ||
      (device.point && device.point.toLowerCase().includes(searchLower)) ||
      (device.pillarno && String(device.pillarno).toLowerCase().includes(searchLower))
    );
  }).sort((a, b) => a.device_id.localeCompare(b.device_id));

  // Open edit modal for a device
  const handleEditDevice = (device: Device) => {
    setIsCreating(false);
    // Cast to DeviceWithCoordinates and handle potential string values
    const deviceWithCoords: DeviceWithCoordinates = {
      ...device,
      lat: device.lat ? parseFloat(String(device.lat)) : 0,
      lng: device.lng ? parseFloat(String(device.lng)) : 0,
      lights: device.lights ? parseInt(String(device.lights)) : 0,
      cpl: device.cpl ? parseFloat(String(device.cpl)) : 0,
    };
    
    setCurrentDevice(deviceWithCoords);
    setFormData(deviceWithCoords);
    setIsEditModalOpen(true);
  };

  // Open create modal
  const handleCreateDevice = () => {
    setIsCreating(true);
    setCurrentDevice(null);
    setFormData({
      device_id: '',
      pillarno: '',
      lng: 23.7275, // Default to Athens
      lat: 37.9838,
      area: '',
      point: '',
      relay: 1,
      lights: 0,
      cpl: 0,
    });
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle select changes
  const handleSelectChange = (value: string, name: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Open map modal to select coordinates
  const handleOpenMapModal = () => {
    setIsMapModalOpen(true);
  };

  // Handle map position selection
  const handlePositionChange = (lat: number, lng: number) => {
    setFormData(prev => ({ 
      ...prev, 
      lat: parseFloat(lat.toFixed(6)),
      lng: parseFloat(lng.toFixed(6))
    }));
  };

  // Save device changes
  const handleSaveDevice = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate form data
      if (!formData.device_id) {
        throw new Error(t('validation.deviceIdRequired'));
      }
      
      // Prepare data for API
      const payload: Record<string, any> = {
        pillarno: formData.pillarno ? parseInt(String(formData.pillarno)) : undefined,
        lng: formData.lng,
        lat: formData.lat,
        area: formData.area,
        point: formData.point,
        relay: parseInt(String(formData.relay)) || 1,
        lights: parseInt(String(formData.lights)) || 0,
        cpl: formData.cpl ? parseFloat(String(formData.cpl)) : 0,
      };
      
      // Remove undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });
      
      let response;
      
      if (isCreating) {
        // Create new device
        payload.device_id = formData.device_id;
        response = await axios.post(
          `${API_BASE_URL}/api/admin/devices`,
          payload,
          getHeaders()
        );
        setSuccess(t('devices.createSuccess'));
      } else {
        // Update existing device
        response = await axios.put(
          `${API_BASE_URL}/api/admin/devices/${formData.device_id}`,
          payload,
          getHeaders()
        );
        setSuccess(t('devices.updateSuccess'));
      }
      
      // Refresh device list
      await deviceStore.loadDevices();
      
      // Close modal after success
      setIsEditModalOpen(false);
    } catch (err: any) {
      console.error('Error saving device:', err);
      setError(err.response?.data?.message || err.message || t('common.unknownError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle time sync action
  const handleTimeSync = async (deviceId: string) => {
    setSyncingDevice(deviceId);
    
    try {
      await deviceStore.syncTime(deviceId);
      
      toast({
        title: t('deviceControl.timeSync'),
        description: t('deviceControl.notifications.timeSyncSuccess'),
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: t('deviceControl.timeSync'),
        description: errorMessage || t('deviceControl.notifications.timeSyncFailed'),
        variant: "destructive"
      });
    } finally {
      setSyncingDevice(null);
    }
  };
  
  // Open reboot confirmation dialog
  const openRebootDialog = (device: Device) => {
    setRebootDevice(device);
    setIsRebootDialogOpen(true);
  };
  
  // Handle reboot action
  const handleReboot = async () => {
    if (!rebootDevice) return;
    
    const deviceId = rebootDevice.device_id;
    setIsRebootDialogOpen(false);
    
    try {
      await deviceStore.rebootDevice(deviceId);
      
      toast({
        title: t('deviceControl.reboot'),
        description: t('deviceControl.notifications.rebootSuccess'),
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: t('deviceControl.reboot'),
        description: errorMessage || t('deviceControl.notifications.rebootFailed'),
        variant: "destructive"
      });
    } finally {
      setRebootDevice(null);
    }
  };

  return (
    <MainLayout>
      <div className="px-2 sm:px-4 py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">{t('devices.management')}</h1>
          {/* <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button 
              onClick={handleCreateDevice}
              className="flex items-center gap-2 flex-1 sm:flex-auto"
            >
              <Plus size={16} />
              {t('devices.createDevice')}
            </Button>
          </div> */}
        </div>
        
        {/* Search input */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <Input
              className="pl-10"
              placeholder={t('devices.searchDevices')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {t('devices.showing')} {filteredDevices.length} {t('devices.ofTotal')} {deviceStore.devices.length} {t('devices.devices')}
          </div>
        </div>
        
        {/* Error alert */}
        {deviceStore.error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{deviceStore.error}</AlertDescription>
          </Alert>
        )}
        
        {/* Devices display - either table or cards based on view mode */}
        {viewMode === 'table' ? (
          // Table view
          <Card>
            <div className="rounded-md border overflow-x-auto device-list-container">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('devices.deviceId')}</TableHead>
                    <TableHead>{t('devices.pillarNo')}</TableHead>
                    <TableHead>{t('devices.area')}</TableHead>
                    <TableHead>{t('devices.point')}</TableHead>
                    <TableHead>{t('devices.coordinates')}</TableHead>
                    <TableHead>{t('devices.relay')}</TableHead>
                    <TableHead>{t('devices.lights')}</TableHead>
                    <TableHead>{t('devices.cpl')}</TableHead>
                    <TableHead>{t('devices.totalPower')}</TableHead>
                    <TableHead className="text-center">{t('devices.actions')}</TableHead>
                    <TableHead className="text-right">{t('common.edit')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deviceStore.loading ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        <Loader2 className="mx-auto animate-spin" size={24} />
                      </TableCell>
                    </TableRow>
                  ) : filteredDevices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        {searchTerm 
                          ? t('devices.noDevicesFoundForSearch') 
                          : t('devices.noDevicesFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDevices.map((device) => (
                      <TableRow key={device.device_id}>
                        <TableCell className="font-medium">{device.device_id}</TableCell>
                        <TableCell>{device.pillarno || '-'}</TableCell>
                        <TableCell>{device.area || '-'}</TableCell>
                        <TableCell>{device.point || '-'}</TableCell>
                        <TableCell>
                          {device.lat && device.lng 
                            ? `${Number(device.lat).toFixed(5)}, ${Number(device.lng).toFixed(5)}` 
                            : '-'}
                        </TableCell>
                        <TableCell>{device.relay || '-'}</TableCell>
                        <TableCell>{device.lights || '-'}</TableCell>
                        <TableCell>
                          {device.cpl 
                            ? Number.parseFloat(String(device.cpl)).toFixed(2) 
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {device.lights && device.cpl 
                            ? `${(Number.parseInt(String(device.lights)) * Number.parseFloat(String(device.cpl))).toFixed(2)} W/h` 
                            : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => handleTimeSync(device.device_id)}
                                    disabled={syncingDevice === device.device_id}
                                  >
                                    {syncingDevice === device.device_id ? (
                                      <Loader2 size={18} className="animate-spin" />
                                    ) : (
                                      <Clock size={18} />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('deviceControl.timeSync')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => openRebootDialog(device)}
                                  >
                                    <RotateCcw size={18} className="text-red-500" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('deviceControl.reboot')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditDevice(device)}
                          >
                            <PenSquare size={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          // Card view for mobile
          <div className="device-grid">
            {deviceStore.loading ? (
              <div className="col-span-full flex justify-center items-center py-12">
                <Loader2 className="animate-spin" size={32} />
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                {searchTerm 
                  ? t('devices.noDevicesFoundForSearch') 
                  : t('devices.noDevicesFound')}
              </div>
            ) : (
              filteredDevices.map((device) => (
                <Card key={device.device_id} className="device-card overflow-hidden">
                  <div className="p-4 border-b bg-muted/30 flex justify-between items-start">
                    <div className="flex-1 mr-2 device-card-header-info">
                      <h3 className="font-medium truncate">{device.device_id}</h3>
                      {device.area && (
                        <div className="text-muted-foreground">
                          {device.area}
                        </div>
                      )}
                      {device.point && (
                        <div className="text-muted-foreground">
                          {device.point}
                        </div>
                      )}
                      {!device.area && !device.point && (
                        <div className="text-muted-foreground">-</div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleEditDevice(device)}
                      className="touch-target"
                    >
                      <PenSquare size={20} />
                    </Button>
                  </div>
                  <div className="p-4 space-y-1">
                    <div className="property-row">
                      <div className="property-label">{t('devices.pillarNo')}</div>
                      <div className="property-value">{device.pillarno || '-'}</div>
                    </div>
                    <div className="property-row">
                      <div className="property-label">{t('devices.relay')}</div>
                      <div className="property-value">{device.relay || '-'}</div>
                    </div>
                    <div className="property-row">
                      <div className="property-label">{t('devices.lights')}</div>
                      <div className="property-value">{device.lights || '-'}</div>
                    </div>
                    <div className="property-row">
                      <div className="property-label">{t('devices.cpl')}</div>
                      <div className="property-value">
                        {device.cpl ? Number.parseFloat(String(device.cpl)).toFixed(2) : '-'}
                      </div>
                    </div>
                    <div className="property-row">
                      <div className="property-label">{t('devices.totalPower')}</div>
                      <div className="property-value">
                        {device.lights && device.cpl 
                          ? `${(Number.parseInt(String(device.lights)) * Number.parseFloat(String(device.cpl))).toFixed(2)} W/h` 
                          : '-'}
                      </div>
                    </div>
                    {device.lat && device.lng && (
                      <div className="property-row">
                        <div className="property-label">{t('devices.coordinates')}</div>
                        <div className="property-value text-xs">
                          {Number(device.lat).toFixed(5)}, {Number(device.lng).toFixed(5)}
                        </div>
                      </div>
                    )}
                    <div className="border-t mt-2 pt-2">
                      <div className="flex items-center justify-between">
                        {/* <div className="property-label">{t('devices.deviceActions')}</div> */}
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleTimeSync(device.device_id)}
                            disabled={syncingDevice === device.device_id}
                            className="h-8 px-2"
                          >
                            {syncingDevice === device.device_id ? (
                              <Loader2 size={14} className="animate-spin mr-1" />
                            ) : (
                              <Clock size={14} className="mr-1" />
                            )}
                            {t('deviceControl.timeSync')}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openRebootDialog(device)}
                            className="h-8 px-2 border-red-200 text-red-500 hover:bg-red-50"
                          >
                            <RotateCcw size={14} className="mr-1" />
                            {t('deviceControl.reboot')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Edit/Create Device Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
          <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <DialogTitle>
              {isCreating ? t('devices.createDevice') : t('devices.editDevice')}
            </DialogTitle>
            <DialogDescription>
              {isCreating 
                ? t('devices.createDeviceDescription') 
                : t('devices.editDeviceDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive" className="mx-4 sm:mx-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="mx-4 sm:mx-6">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}
          
          <div className="px-4 sm:px-6 py-2 sm:py-4 overflow-y-auto max-h-[60vh]">
            <div className="space-y-4">
              {/* Device ID (readonly when editing) */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="device_id" className={isMobile ? "" : "text-right"}>
                  {t('devices.deviceId')}*
                </Label>
                <Input
                  id="device_id"
                  name="device_id"
                  value={formData.device_id}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                  disabled={!isCreating}
                  required
                />
              </div>
              
              {/* Pillar Number */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="pillarno" className={isMobile ? "" : "text-right"}>
                  {t('devices.pillarNo')}
                </Label>
                <Input
                  id="pillarno"
                  name="pillarno"
                  type="number"
                  value={formData.pillarno}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                />
              </div>
              
              {/* Area */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="area" className={isMobile ? "" : "text-right"}>
                  {t('devices.area')}
                </Label>
                <Input
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                />
              </div>
              
              {/* Point */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="point" className={isMobile ? "" : "text-right"}>
                  {t('devices.point')}
                </Label>
                <Input
                  id="point"
                  name="point"
                  value={formData.point}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                />
              </div>
              
              {/* Coordinates */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <div className={isMobile ? "flex justify-between items-center" : "text-right"}>
                  <Label>{t('devices.coordinates')}</Label>
                  {isMobile && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleOpenMapModal}
                      title={t('devices.pickFromMap')}
                      className="touch-target"
                    >
                      <MapPin size={16} className="mr-2" />
                      {t('devices.map')}
                    </Button>
                  )}
                </div>
                <div className={isMobile ? "grid grid-cols-2 gap-2" : "col-span-3 flex items-center gap-2"}>
                  <Input
                    id="lat"
                    name="lat"
                    type="number"
                    step="0.000001"
                    value={formData.lat}
                    onChange={handleInputChange}
                    placeholder="Latitude"
                    className="flex-1"
                  />
                  <Input
                    id="lng"
                    name="lng"
                    type="number"
                    step="0.000001"
                    value={formData.lng}
                    onChange={handleInputChange}
                    placeholder="Longitude"
                    className="flex-1"
                  />
                  {!isMobile && (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleOpenMapModal}
                      title={t('devices.pickFromMap')}
                    >
                      <MapPin size={18} />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Relay */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="relay" className={isMobile ? "" : "text-right"}>
                  {t('devices.relay')}
                </Label>
                <Select
                  value={String(formData.relay)}
                  onValueChange={(value) => handleSelectChange(value, 'relay')}
                >
                  <SelectTrigger className={isMobile ? "w-full" : "col-span-3"}>
                    <SelectValue placeholder={t('devices.selectRelay')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Lights */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="lights" className={isMobile ? "" : "text-right"}>
                  {t('devices.lights')}
                </Label>
                <Input
                  id="lights"
                  name="lights"
                  type="number"
                  value={formData.lights || ''}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                />
              </div>
              
              {/* CPL (Watt) */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label htmlFor="cpl" className={isMobile ? "" : "text-right"}>
                  {t('devices.cpl')}
                </Label>
                <Input
                  id="cpl"
                  name="cpl"
                  type="number"
                  step="0.01"
                  value={formData.cpl || ''}
                  onChange={handleInputChange}
                  className={isMobile ? "w-full" : "col-span-3"}
                />
              </div>
              
              {/* Total Power (Virtual Field) */}
              <div className={isMobile ? "space-y-2" : "grid grid-cols-4 items-center gap-4"}>
                <Label className={isMobile ? "" : "text-right"}>
                  {t('devices.totalPower')}
                </Label>
                <div className={isMobile ? "" : "col-span-3"}>
                  <Input
                    readOnly
                    value={formData.lights && formData.cpl ? `${(formData.lights * formData.cpl).toFixed(2)} Wh` : ''}
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="p-4 sm:p-6 bg-muted/30 border-t flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <div className="flex-1 order-2 sm:order-1">
              <DialogClose asChild>
                <Button variant="outline" disabled={isLoading} className="w-full sm:w-auto">
                  <X size={16} className="mr-2" />
                  {t('common.cancel')}
                </Button>
              </DialogClose>
            </div>
            <div className="order-1 sm:order-2">
              <Button onClick={handleSaveDevice} disabled={isLoading} className="w-full sm:w-auto">
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check size={16} className="mr-2" />
                )}
                {t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Map Selection Modal */}
      <Dialog open={isMapModalOpen} onOpenChange={setIsMapModalOpen}>
        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
          <DialogHeader className="p-4 sm:p-6">
            <DialogTitle>{t('devices.selectLocationOnMap')}</DialogTitle>
            <DialogDescription>
              {t('devices.clickOnMapToSelectCoordinates')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="px-4 sm:px-6">
            <MapPicker 
              initialPosition={[formData.lat || 37.9838, formData.lng || 23.7275]} 
              onPositionChange={handlePositionChange} 
            />
            
            <div className="flex justify-between mt-2 mb-4">
              <div className="text-sm text-muted-foreground">
                {t('devices.selectedCoordinates')}: {formData.lat?.toFixed(6)}, {formData.lng?.toFixed(6)}
              </div>
            </div>
          </div>
          
          <DialogFooter className="p-4 sm:p-6 bg-muted/30 border-t">
            <DialogClose asChild>
              <Button className="w-full sm:w-auto">
                {t('common.done')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reboot Confirmation Dialog */}
      <AlertDialog open={isRebootDialogOpen} onOpenChange={setIsRebootDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {t('deviceControl.reboot')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('deviceControl.notifications.rebootConfirmation', { 
                deviceId: rebootDevice?.device_id || '' 
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReboot}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('deviceControl.reboot')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
});

export default DeviceManagementPage; 